import axios from "axios";
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";

/* ═══════════════════════════════════════════════════════════════
   DATA LAYER ONLY – no UI here.
   Each master describes:
     tableName : table used by GetDataFromGivenTableName / Delete…
     endpoint  : POST /api/Master/<endpoint>   (body is an ARRAY)
     fields    : payload keys. `key:true` fields identify a row
                 (used to build the delete WHERE clause).
   ⚠ Table names follow your notes (company_mast, group_mast,
     item_master, size_mast, colour_mast, fitting_mast, sleves_mast).
     If any table is named differently in the DB, change it here only.
   ═══════════════════════════════════════════════════════════════ */

const F = (name, type = "string", extra = {}) => ({ name, type, ...extra });

export const MASTERS = {
  brand: {
    tableName: "COMPANY_MAST",
    endpoint: "CompanyMast",
    fields: [
      F("compName", "string", { key: true, upper: true }),
      F("compCode", "string", { upper: true }),
      F("disper", "number"),
      F("btype", "string", { upper: true }),
    ],
  },
  group: {
    tableName: "GROUP_MAST",
    endpoint: "GroupMast",
    fields: [
      F("groupName", "string", { key: true, upper: true }),
      F("effectdiscount", "bool"),
      F("hsncode", "string"),
    ],
  },
  size: {
    tableName: "SIZE_MAST",
    endpoint: "SizeMast",
    fields: [F("prodSize", "string", { key: true, upper: true })],
  },
  colour: {
    tableName: "COLOUR_MAST",
    endpoint: "ColourMast",
    fields: [F("colour", "string", { key: true, upper: true })],
  },
  fitting: {
    tableName: "FITTING_MAST",
    endpoint: "FittingMast",
    fields: [F("fitting", "string", { key: true, upper: true })],
  },
  sleeves: {
    tableName: "SLEVES_MAST",
    endpoint: "SlevesMast",
    fields: [F("sleves", "string", { key: true, upper: true })],
  },
  item: {
    tableName: "ITEM_MASTER",
    endpoint: "ItemMaster",
    fields: [
      F("pDate", "date"),
      F("compName", "string", { upper: true }),
      F("groupName", "string", { upper: true }),
      F("designCode", "string", { key: true, upper: true }),
      F("code"),
      F("code1"),
      F("prodName", "string", { key: true, upper: true }),
      F("prodSize", "string", { upper: true }),
      F("colour", "string", { upper: true }),
      F("fitting", "string", { upper: true }),
      F("sleeves", "string", { upper: true }),
      F("disPer", "number"),
      F("dPrice", "number"),
      F("cPrice", "number"),
      F("oStock", "number"),
      F("rQty", "number"),
      F("billno"),
      F("itemcode"),
      F("taans"),
      F("hsncode"),
      F("resellerprice", "number"),
      F("ptype"),
      F("gst", "number"),
      F("packpcs", "number"),
    ],
  },
};

/* ── helpers ─────────────────────────────────────────────────── */
const headers = () => ({
  tenantName:
    localStorage.getItem("tenantName") ||
    sessionStorage.getItem("tenantName") ||
    "",
});

// case-insensitive property lookup (API may return STATENAME / statename …)
const pick = (item, name) => {
  const k = Object.keys(item || {}).find(
    (x) => x.toLowerCase() === name.toLowerCase(),
  );
  return k ? item[k] : undefined;
};

const escapeSql = (v) => String(v ?? "").replace(/'/g, "''");

const normalise = (item, master, idx) => {
  const row = { key: idx };
  master.fields.forEach((f) => {
    let v = pick(item, f.name);
    if (f.type === "number") v = Number(v) || 0;
    else if (f.type === "bool")
      v =
        v === true ||
        v === 1 ||
        ["true", "1", "yes"].includes(String(v).toLowerCase());
    else v = v == null || v === "" ? "-" : String(v);
    row[f.name] = v;
  });
  return row;
};

export const buildPayload = (master, values = {}, old = null) => {
  const out = {};
  master.fields.forEach((f) => {
    let v = values[f.name] !== undefined ? values[f.name] : old?.[f.name];
    if (f.type === "number") v = Number(v) || 0;
    else if (f.type === "bool") v = !!v;
    else if (f.type === "date")
      v =
        old?.[f.name] && old[f.name] !== "-"
          ? old[f.name]
          : new Date().toISOString();
    else {
      v = String(v ?? "").trim();
      if (f.upper) v = v.toUpperCase();
      if (!v) v = "-";
    }
    out[f.name] = v;
  });
  out.cloud_upload = "true";
  return [out]; // swagger bodies are arrays
};

/* ── GET ─────────────────────────────────────────────────────── */
export const fetchMaster = async (master) => {
  const res = await axios.get(
    `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName`,
    { params: { tableName: master.tableName }, headers: headers() },
  );
  return (Array.isArray(res.data) ? res.data : []).map((item, idx) =>
    normalise(item, master, idx),
  );
};

/* ── ADD ─────────────────────────────────────────────────────── */
export const addMaster = (master, values, old = null) =>
  axios.post(
    `${CREATE_jwel}/api/Master/${master.endpoint}`,
    buildPayload(master, values, old),
    { headers: headers() },
  );

/* ── DELETE ──────────────────────────────────────────────────── */
export const deleteMaster = (master, row) => {
  const where = master.fields
    .filter((f) => f.key)
    .map((f) => `${f.name.toLowerCase()}='${escapeSql(row[f.name])}'`)
    .join(" AND ");
  return axios.post(
    `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=${master.tableName}&where=${encodeURIComponent(where)}`,
    {},
    { headers: headers() },
  );
};

/* ── UPDATE = delete old + insert new (same as StateMaster) ───
   If the insert fails after the delete, the old row is restored. */
export const updateMaster = async (master, oldRow, values) => {
  await deleteMaster(master, oldRow);
  try {
    await addMaster(master, values, oldRow);
  } catch (err) {
    try {
      await addMaster(master, oldRow, oldRow); // rollback
    } catch {
      /* ignore – surface original error */
    }
    throw err;
  }
};

/* duplicate check on key fields (case-insensitive) */
export const isDuplicate = (master, rows, values, ignoreKey = null) => {
  const keys = master.fields.filter((f) => f.key);
  return rows.some(
    (r) =>
      r.key !== ignoreKey &&
      keys.every(
        (f) =>
          String(r[f.name]).trim().toUpperCase() ===
          String(values[f.name] ?? "")
            .trim()
            .toUpperCase(),
      ),
  );
};

/* ── CODE MASTER (code_configure) ────────────────────────────── */
export const fetchCodeConfigure = async () => {
  const baseUrl = CREATE_jwel1 || "https://tssnewservices.timeserasoftware.in";
  const res = await axios.get(
    `${baseUrl}/api/Master/GetDataFromGivenTableName`,
    {
      params: { tableName: "code_configure" },
      headers: headers(),
    }
  );
  return Array.isArray(res.data) ? res.data : [];
};

export const updateCodeConfigure = async ({ updateString, whereString }) => {
  const baseUrl = CREATE_jwel1 || "https://tssnewservices.timeserasoftware.in";
  return axios.post(
    `${baseUrl}/api/Master/CommonUpdateWithWhere`,
    {
      tableName: "code_configure",
      updateString,
      whereString,
    },
    {
      headers: headers(),
    }
  );
};

export const updateMasterWithWhere = async (tableName, updateString, whereString) => {
  const baseUrl = CREATE_jwel1 || "https://tssnewservices.timeserasoftware.in";
  return axios.post(
    `${baseUrl}/api/Master/CommonUpdateWithWhere`,
    {
      tableName,
      updateString,
      whereString,
    },
    {
      headers: headers(),
    }
  );
};
